import {
  WEBSITE_HERO_DECODE_TIMEOUT,
  WEBSITE_HERO_MAX_BYTES,
  WEBSITE_HERO_MAX_DIMENSION,
  WEBSITE_HERO_MAX_PIXELS,
  WEBSITE_HERO_ROOT,
  WEBSITE_HERO_STORAGE_BUCKET,
} from '../config/websiteHero';
import { WebsiteHeroContentType, WebsiteHeroDetails } from '../types/websiteHero.types';

const extensions: Record<WebsiteHeroContentType, string> = {
  'image/webp': 'webp', 'image/png': 'png', 'image/jpeg': 'jpg',
};

export const isWebsiteHeroId = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value);

export const isWebsiteHeroContentType = (value: unknown): value is WebsiteHeroContentType =>
  value === 'image/webp' || value === 'image/png' || value === 'image/jpeg';

export function isWebsiteHeroStoragePath(value: unknown, id?: string): value is string {
  if (typeof value !== 'string') return false;
  const parts = value.split('/');
  return parts.length === 3 && parts[0] === WEBSITE_HERO_ROOT &&
    isWebsiteHeroId(parts[1]) && (!id || parts[1] === id) &&
    /^[A-Za-z0-9_-]{1,128}\.(webp|png|jpg)$/.test(parts[2]);
}

/** Do not accept legacy Media URLs, external URLs, or a different Firebase bucket. */
export function isWebsiteHeroImageUrl(value: unknown, id?: string, storagePath?: string): value is string {
  if (typeof value !== 'string' || !WEBSITE_HERO_STORAGE_BUCKET) return false;
  try {
    const url = new URL(value);
    const match = url.pathname.match(/^\/v0\/b\/([^/]+)\/o\/([^/]+)$/);
    if (url.protocol !== 'https:' || url.hostname !== 'firebasestorage.googleapis.com' ||
      url.port || url.username || url.password || url.hash || !match) return false;
    const bucket = decodeURIComponent(match[1]);
    const path = decodeURIComponent(match[2]);
    return bucket === WEBSITE_HERO_STORAGE_BUCKET && isWebsiteHeroStoragePath(path, id) &&
      (storagePath === undefined || path === storagePath) && url.searchParams.get('alt') === 'media';
  } catch {
    return false;
  }
}

export function validateWebsiteHeroDetails(details: WebsiteHeroDetails): WebsiteHeroDetails {
  const name = typeof details.name === 'string' ? details.name.trim() : '';
  if (!name || name.length > 120) throw new Error('Enter a name between 1 and 120 characters.');
  if (!Number.isSafeInteger(details.sortOrder) || details.sortOrder < 0 || details.sortOrder > 1000000) {
    throw new Error('Sort order must be a whole number between 0 and 1000000.');
  }
  return { name, sortOrder: details.sortOrder };
}

function readHeader(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reader.onload = reader.onerror = reader.onabort = null;
      if (error) {
        if (reader.readyState === FileReader.LOADING) reader.abort();
        reject(error);
      } else if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
      } else reject(new Error('The image file could not be read.'));
    };
    const timer = setTimeout(() => finish(new Error('Reading the image timed out.')), WEBSITE_HERO_DECODE_TIMEOUT);
    reader.onload = () => finish();
    reader.onerror = reader.onabort = () => finish(new Error('The image file could not be read.'));
    try { reader.readAsArrayBuffer(file.slice(0, 16)); }
    catch { finish(new Error('The image file could not be read.')); }
  });
}

function signatureType(bytes: Uint8Array): WebsiteHeroContentType | undefined {
  const png = [137, 80, 78, 71, 13, 10, 26, 10];
  if (bytes.length >= 8 && png.every((byte, index) => bytes[index] === byte)) return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255) return 'image/jpeg';
  const text = (from: number, to: number) => String.fromCharCode(...Array.from(bytes.slice(from, to)));
  if (bytes.length >= 12 && text(0, 4) === 'RIFF' && text(8, 12) === 'WEBP') return 'image/webp';
  return undefined;
}

/** Detached image preparation with bounded download/decode and complete cleanup. */
export function decodeWebsiteHeroImage(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    let settled = false;
    let decoding = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      image.onload = image.onerror = null;
      const dimensions = { width: image.naturalWidth, height: image.naturalHeight };
      image.removeAttribute('src');
      if (error) reject(error);
      else resolve(dimensions);
    };
    const timer = setTimeout(() => finish(new Error('Image loading or decoding timed out.')), WEBSITE_HERO_DECODE_TIMEOUT);
    image.decoding = 'async';
    image.onerror = () => finish(new Error('The image could not be loaded.'));
    image.onload = async () => {
      if (settled || decoding) return;
      decoding = true;
      try {
        if (typeof image.decode !== 'function') throw new Error('Image decoding is not supported by this browser.');
        await image.decode();
        if (settled) return;
        const { naturalWidth: width, naturalHeight: height } = image;
        if (!image.complete || width <= 0 || height <= 0) throw new Error('The image has no usable dimensions.');
        if (width > WEBSITE_HERO_MAX_DIMENSION || height > WEBSITE_HERO_MAX_DIMENSION || width * height > WEBSITE_HERO_MAX_PIXELS) {
          throw new Error(`Image dimensions must be no larger than ${WEBSITE_HERO_MAX_DIMENSION}px on either side.`);
        }
        finish();
      } catch (error) {
        finish(error instanceof Error ? error : new Error('The image could not be decoded.'));
      }
    };
    try { image.src = url; }
    catch { finish(new Error('The image could not be loaded.')); }
  });
}

export async function prepareWebsiteHeroUpload(file: File): Promise<{
  contentType: WebsiteHeroContentType; extension: string; width: number; height: number;
}> {
  if (!isWebsiteHeroContentType(file.type)) throw new Error('Choose a WebP, JPEG or PNG image. WebM video is not supported.');
  if (file.size <= 0 || file.size > WEBSITE_HERO_MAX_BYTES) throw new Error('Choose an image larger than zero bytes and no larger than 5 MB.');
  const contentType = signatureType(await readHeader(file));
  if (!contentType || contentType !== file.type) throw new Error('The image contents do not match its declared WebP, JPEG or PNG format.');
  const url = URL.createObjectURL(file);
  try {
    const dimensions = await decodeWebsiteHeroImage(url);
    return { contentType, extension: extensions[contentType], ...dimensions };
  } finally {
    URL.revokeObjectURL(url);
  }
}
